import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  LayoutDashboard,
  Users,
  Building2,
  DollarSign,
  Trophy,
  Swords,
  ClipboardList,
  Ticket,
  Tag,
  ScanLine,
  CreditCard,
  UserCog,
  LogOut,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";
import logo from "@/assets/fpf3-logo-new.png";

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badgeKey?: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Geral",
    items: [
      { label: "Dashboard", href: "/admin/dashboard", icon: <LayoutDashboard size={16} /> },
    ],
  },
  {
    label: "Federação",
    items: [
      { label: "Atletas Federados", href: "/admin/federacao/atletas", icon: <Users size={16} />, badgeKey: "athletes" },
      { label: "Boxes Afiliados", href: "/admin/federacao/boxes", icon: <Building2 size={16} />, badgeKey: "boxes" },
    ],
  },
  {
    label: "Financeiro",
    items: [
      { label: "Dashboard Financeiro", href: "/admin/financeiro", icon: <DollarSign size={16} /> },
    ],
  },
  {
    label: "Eventos",
    items: [
      { label: "Gestão de Eventos", href: "/admin/eventos", icon: <Trophy size={16} /> },
      { label: "WodArena", href: "/admin/wod-arena", icon: <Swords size={16} /> },
      { label: "Candidaturas 2027", href: "/admin/candidaturas", icon: <ClipboardList size={16} />, badgeKey: "candidaturas" },
    ],
  },
  {
    label: "Ingressos",
    items: [
      { label: "Vendas", href: "/admin/ingressos/vendas", icon: <Ticket size={16} /> },
      { label: "Tipos de Ingresso", href: "/admin/ingressos/tipos", icon: <Tag size={16} /> },
      { label: "Scanner QR", href: "/admin/ingressos/scanner", icon: <ScanLine size={16} /> },
    ],
  },
  {
    label: "Pagamentos",
    items: [
      { label: "MercadoPago", href: "/admin/pagamentos/mercado-pago", icon: <CreditCard size={16} /> },
    ],
  },
  {
    label: "Sistema",
    items: [
      { label: "Usuários", href: "/admin/usuarios", icon: <UserCog size={16} /> },
    ],
  },
];

function usePendingCounts() {
  const { data: boxesPending = 0 } = useQuery({
    queryKey: ["admin-sidebar-boxes-pending"],
    queryFn: async () => {
      const { count } = await (supabase as any)
        .from("box_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending");
      return count ?? 0;
    },
    staleTime: 60_000,
  });

  const { data: athletesPending = 0 } = useQuery({
    queryKey: ["admin-sidebar-athletes-pending"],
    queryFn: async () => {
      const { count } = await supabase
        .from("athletes")
        .select("id", { count: "exact", head: true })
        .is("user_id", null);
      return count ?? 0;
    },
    staleTime: 60_000,
  });

  const { data: candidaturasNew = 0 } = useQuery({
    queryKey: ["admin-sidebar-candidaturas-new"],
    queryFn: async () => {
      const { count } = await (supabase as any)
        .from("competition_applications")
        .select("id", { count: "exact", head: true })
        .eq("status", "recebida");
      return count ?? 0;
    },
    staleTime: 60_000,
  });

  return { boxes: boxesPending, athletes: athletesPending, candidaturas: candidaturasNew };
}

interface AdminSidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function AdminSidebar({ mobileOpen, onMobileClose }: AdminSidebarProps) {
  const { user, role, signOut } = useAuth();
  const navigate = useNavigate();
  const badges = usePendingCounts();

  const badgeMap: Record<string, number> = {
    boxes: badges.boxes,
    athletes: badges.athletes,
    candidaturas: badges.candidaturas,
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-[hsl(var(--sidebar-background))] border-r border-[hsl(var(--sidebar-border))]">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-[hsl(var(--sidebar-border))]">
        <img src={logo} alt="FPF3" className="h-8 w-auto" />
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-primary">FPF3</span>
          <span className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">
            Centro de Gestão
          </span>
        </div>
        {/* Mobile close button */}
        <button
          onClick={onMobileClose}
          className="ml-auto md:hidden p-1 text-muted-foreground hover:text-foreground"
        >
          <X size={18} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
        {NAV_GROUPS.map((group) => {
          // Filtra itens com base nas permissões da role
          const filteredItems = group.items.filter((item) => {
            if (item.href === "/admin/usuarios") {
              return role === "admin";
            }
            return true;
          });

          if (filteredItems.length === 0) return null;

          return (
            <div key={group.label}>
              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-muted-foreground/60 px-2 mb-1.5">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {filteredItems.map((item) => {
                  const count = item.badgeKey ? badgeMap[item.badgeKey] : 0;
                  return (
                    <li key={item.href}>
                      <NavLink
                        to={item.href}
                        onClick={onMobileClose}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                            isActive
                              ? "bg-primary/10 text-primary border border-primary/20"
                              : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--sidebar-accent))]"
                          }`
                        }
                      >
                        <span className="flex-shrink-0">{item.icon}</span>
                        <span className="flex-1 truncate">{item.label}</span>
                        {count > 0 && (
                          <span className="flex-shrink-0 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-black bg-primary text-primary-foreground rounded-full px-1">
                            {count > 99 ? "99+" : count}
                          </span>
                        )}
                        <ChevronRight
                          size={12}
                          className="flex-shrink-0 opacity-0 group-hover:opacity-40 transition-opacity"
                        />
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-[hsl(var(--sidebar-border))] px-3 py-4 space-y-1">
        <div className="px-3 py-2">
          <p className="text-xs font-semibold text-foreground truncate">
            {user?.email ?? "Administrador"}
          </p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">
            {role === "admin" ? "Admin" : role === "organizer" ? "Organizador" : role === "staff" ? "Staff" : "Atleta"}
          </p>
        </div>
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
        >
          <LogOut size={16} />
          <span>Sair do sistema</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 flex-shrink-0 h-screen sticky top-0">
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          <aside className="relative z-50 flex flex-col w-72 h-full">
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}

export function AdminMobileTopBar({ onMenuOpen }: { onMenuOpen: () => void }) {
  return (
    <div className="md:hidden flex items-center gap-3 px-4 py-3 bg-[hsl(var(--sidebar-background))] border-b border-[hsl(var(--sidebar-border))] sticky top-0 z-40">
      <button
        onClick={onMenuOpen}
        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary/80 rounded-lg transition-all"
      >
        <Menu size={20} />
      </button>
      <img src={logo} alt="FPF3" className="h-6 w-auto" />
      <span className="text-xs font-black uppercase tracking-wider text-muted-foreground">
        Centro de Gestão
      </span>
    </div>
  );
}
